import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize, fromEvent } from 'rxjs';

import {
  PLAY_REQUEST_MESSAGE_MAX,
  PLAY_REQUEST_RETRY_FALLBACK_S,
  PLAYERS_PRIVACY_FRAGMENT,
  PLAYERS_PRIVACY_PATH,
  PLAYERS_PROFILE_PATH,
} from '../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../shared/players/players.copy';
import type { PlayerCard } from '../../../../shared/players/players.model';
import { PlayersService } from '../../../../shared/players/players.service';
import { serverMessage } from '../../../../shared/players/players.utils';
import { playersSessionIdentity } from '../../../../shared/players/players-session';
import { retryAfterSeconds } from '../../../../shared/services/newsletter.service';

/** Si quien invita puede enviar (B21: perfil con consentimiento y sin suspender). */
export type PlayRequestSender = 'can-send' | 'need-profile' | 'suspended';

type FieldKey = 'game' | 'message';

/**
 * Modal «Invitar a jugar» (B11 J3, D32). Vive mientras está abierto: el directorio lo
 * monta con la tarjeta elegida y lo desmonta al cerrar. Patrón de diálogo de la casa
 * (`coverage-notice-dialog`): foco dentro al abrir, Tab atrapado, scroll del fondo
 * bloqueado restaurando su valor previo, y foco de vuelta al botón que lo abrió. Aquí sí
 * cierra con Escape y con el fondo. Solo en navegador.
 *
 * Campos: juego (los de esa tarjeta, o ninguno), mensaje en texto plano ≤ 280 y el
 * honeypot `website` (patrón W3 del newsletter), que solo viaja con valor. Sin casilla de
 * consentimiento: para enviar, la API ya exige un perfil con consentimiento sellado (B9,
 * desviación de §5.6.3 anotada en la ADDENDA J3); bajo el botón, un aviso informativo.
 *
 * Atado a la sesión (como la pestaña, H1; época de Astra J3 H3): una respuesta que llega con
 * otra identidad, o tras A → B → A, se descarta.
 */
@Component({
  selector: 'app-play-request-modal',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './play-request-modal.component.html',
  styleUrl: './play-request-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayRequestModalComponent {
  private readonly players = inject(PlayersService);
  private readonly session = playersSessionIdentity();
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly card = input.required<PlayerCard>();
  readonly sender = input<PlayRequestSender>('can-send');
  /** 201 (también el del honeypot): el directorio marca la tarjeta como `sent`. */
  readonly sent = output<{ alias: string; message: string }>();
  /** 404: ese jugador ya no está; el directorio se recarga al cerrar. */
  readonly notFound = output<void>();
  readonly closed = output<void>();

  protected readonly copy = PLAYERS_COPY.requests;
  protected readonly suspendedCopy = PLAYERS_COPY.banner;
  protected readonly messageMax = PLAY_REQUEST_MESSAGE_MAX;
  protected readonly profilePath = PLAYERS_PROFILE_PATH;
  protected readonly privacyPath = PLAYERS_PRIVACY_PATH;
  protected readonly privacyFragment = PLAYERS_PRIVACY_FRAGMENT;

  protected readonly form = inject(FormBuilder).nonNullable.group({
    game: '',
    message: '',
    website: '',
  });

  protected readonly sending = signal(false);
  protected readonly coolingDown = signal(false);
  protected readonly fieldErrors = signal<Partial<Record<FieldKey, string>>>({});
  protected readonly generalError = signal<{ text: string; profileCta: boolean } | null>(null);
  protected readonly messageLength = signal(0);

  protected readonly titleId = 'play-request-modal-title';
  protected readonly canSubmit = computed(() => !this.sending() && !this.coolingDown());

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocused: HTMLElement | null = null;
  private previousOverflow = '';
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private wasNotFound = false;

  constructor() {
    if (!this.isBrowser) return;
    this.previouslyFocused = (this.document.activeElement as HTMLElement | null) ?? null;
    this.previousOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';

    afterNextRender(() => {
      const root = this.dialogRef()?.nativeElement;
      if (root) (this.focusables(root)[0] ?? root).focus();
    });

    fromEvent<KeyboardEvent>(this.document, 'keydown')
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.onKeydown(event));

    this.destroyRef.onDestroy(() => {
      if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
      this.document.body.style.overflow = this.previousOverflow;
      const origin = this.previouslyFocused;
      if (origin && this.document.contains(origin)) {
        queueMicrotask(() => origin.focus({ preventScroll: true }));
      }
    });
  }

  protected close(): void {
    if (this.wasNotFound) this.notFound.emit();
    this.closed.emit();
  }

  protected onMessageInput(): void {
    this.messageLength.set(this.form.controls.message.value.length);
  }

  protected submit(): void {
    if (!this.canSubmit() || this.sender() !== 'can-send') return;
    const value = this.form.getRawValue();
    const alias = this.card().alias;
    // Época de sesión (Astra J3 H3): A → B → A con el envío en vuelo no lo valida.
    const account = this.session.accountId();
    const epoch = this.session.epoch();
    const sameAccount = () => this.session.epoch() === epoch && this.session.accountId() === account;
    this.sending.set(true);
    this.fieldErrors.set({});
    this.generalError.set(null);
    this.players
      .sendRequest(alias, {
        game: value.game || undefined,
        message: value.message.trim() || undefined,
        website: value.website || undefined,
      })
      .pipe(
        finalize(() => this.sending.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (message) => {
          if (!sameAccount()) return;
          this.sent.emit({ alias, message: message ?? this.copy.sentFallback });
        },
        error: (err: unknown) => {
          if (!sameAccount()) return;
          this.showError(err);
        },
      });
  }

  private showError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) {
      this.generalError.set({ text: this.copy.sendError, profileCta: false });
      return;
    }
    if (err.status === 422) {
      const errors = (err.error as { error?: unknown } | null)?.error;
      const fields: Partial<Record<FieldKey, string>> = {};
      let general: { text: string; profileCta: boolean } | null = null;
      if (errors && typeof errors === 'object') {
        for (const [key, messages] of Object.entries(errors as Record<string, unknown>)) {
          const first = Array.isArray(messages) ? messages[0] : messages;
          if (typeof first !== 'string' || first.trim() === '') continue;
          if (key === 'game' || key === 'message') fields[key] = first;
          else general ??= { text: first, profileCta: key === 'sender' && this.leadsToProfile(first) };
        }
      }
      this.fieldErrors.set(fields);
      if (!general && Object.keys(fields).length === 0) {
        general = { text: this.copy.sendError, profileCta: false };
      }
      this.generalError.set(general);
      return;
    }
    if (err.status === 404) {
      this.wasNotFound = true;
      this.generalError.set({ text: serverMessage(err) ?? this.copy.notFoundFallback, profileCta: false });
      return;
    }
    if (err.status === 429) {
      this.generalError.set({ text: serverMessage(err) ?? this.copy.rateLimitedFallback, profileCta: false });
      const seconds = retryAfterSeconds(err.headers.get('Retry-After')) ?? PLAY_REQUEST_RETRY_FALLBACK_S;
      this.startCooldown(seconds);
      return;
    }
    this.generalError.set({ text: this.copy.sendError, profileCta: false });
  }

  /**
   * El CTA a `/perfil/comunidad` solo ayuda con `NEED_PROFILE` o `NEED_VERIFIED`; con
   * `SUSPENDED`, solo el texto (auditoría de Alma, STOP intermedio de J3). La API decide por
   * la clave `sender` y su copy es el de `PlayersCopy`, verbatim: se distingue por el texto.
   */
  private leadsToProfile(text: string): boolean {
    return text === this.copy.needProfileFallback || text === this.copy.needVerifiedText;
  }

  private startCooldown(seconds: number): void {
    if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
    this.coolingDown.set(true);
    this.cooldownTimer = setTimeout(() => {
      this.cooldownTimer = null;
      this.coolingDown.set(false);
    }, seconds * 1000);
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const root = this.dialogRef()?.nativeElement;
    if (!root) return;
    const focusables = this.focusables(root);
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.document.activeElement as HTMLElement | null;
    if (!active || !root.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Enfocables del diálogo en orden DOM, sin el honeypot (`tabindex="-1"`). */
  private focusables(root: HTMLElement): HTMLElement[] {
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.getAttribute('tabindex') !== '-1' && !el.closest('[aria-hidden="true"]'));
  }
}
